import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AppDialog from "components/Dialog/AppDialog";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { NDIS_SUPPORT_CATEGORY, MEASUREMENT_METHOD, GOAL_STATUS, SUPPORT_CATEGORY_SUBCATEGORY } from "config/AppConfig";
import moment from "moment";

const useStyles = makeStyles(() => ({
  sectionTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: AppColor.mainColor,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    margin: "10px 0 15px",
    display: "block",
  },
  fieldContainer: {
    marginBottom: 16,
    padding: "12px 16px",
    // backgroundColor: "#F8FAFC",
    borderRadius: 8,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#000000",
    marginBottom: 6,
    letterSpacing: "0.03em",
  },
  fieldValue: {
    fontSize: 13,
    color: "#111827",
    lineHeight: 1.5,
  },
  milestoneRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 8,
    padding: "10px 14px",
    backgroundColor: "#F0FDF4",
    borderRadius: 8,
  },
  milestoneIcon: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    flexShrink: 0,
  },
  milestoneCompleted: {
    backgroundColor: "#22C55E",
    color: "#FFFFFF",
  },
  milestonePending: {
    backgroundColor: "#E5E7EB",
    color: "#6B7280",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 10,
    gap: 8,
  },
}));

const ViewGoalDetailModal = ({ open, onClose, goal, questions = [] }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!goal) return null;

  const getCategoryLabel = (category) => {
    switch (category) {
      case NDIS_SUPPORT_CATEGORY.CORE_SUPPORTS:
        return t("goalCategory.CORE_SUPPORTS", "Core Supports");
      case NDIS_SUPPORT_CATEGORY.CAPACITY_BUILDING:
        return t("goalCategory.CAPACITY_BUILDING", "Capacity Building");
      case NDIS_SUPPORT_CATEGORY.CAPITAL_SUPPORTS:
        return t("goalCategory.CAPITAL_SUPPORTS", "Capital Supports");
      default:
        return category;
    }
  };

  const getSubCategoryLabel = (subCategory) => {
    switch (subCategory) {
      case SUPPORT_CATEGORY_SUBCATEGORY.DAILY_ACTIVITIES:
        return "Daily Activities";
      case SUPPORT_CATEGORY_SUBCATEGORY.COMMUNITY_PARTICIPATION:
        return "Community Participation";
      case SUPPORT_CATEGORY_SUBCATEGORY.SIL:
        return "SIL";
      default:
        return subCategory;
    }
  };

  const getMeasurementLabel = (method) => {
    switch (method) {
      case MEASUREMENT_METHOD.MILESTONE:
        return t("goalMeasurement.MILESTONE", "Milestone");
      case MEASUREMENT_METHOD.PERCENTAGE:
        return t("goalMeasurement.PERCENTAGE", "Percentage");
      case MEASUREMENT_METHOD.FREQUENCY_COUNT:
        return t("goalMeasurement.FREQUENCY_COUNT", "Frequency Count");
      case MEASUREMENT_METHOD.OBSERVATION:
        return t("goalMeasurement.OBSERVATION", "Observation");
      default:
        return method;
    }
  };

  const getLinkedQuestionLabel = (questionId) => {
    if (!questionId) return "-";
    const question = questions.find(q => q.id === questionId);
    return question?.question || "-";
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t("goal.view_title", "View Goal Details")}
      maxWidth="md"
    >
      <Box>
        <Box style={{ padding: 10 }}>
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.name")}
                </Typography>
                <Typography className={classes.fieldValue}>
                  {goal.name}
                </Typography>
              </Box>
            </div>

            <div className="col-sm-12 col-md-6">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.ndis_category")}
                </Typography>
                <Typography className={classes.fieldValue}>
                  {getCategoryLabel(goal.category)}
                </Typography>
              </Box>
            </div>

            <div className="col-sm-12 col-md-6">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.subCategory")}
                </Typography>
                <Typography className={classes.fieldValue}>
                  {getSubCategoryLabel(goal.subCategory)}
                </Typography>
              </Box>
            </div>

            <div className="col-sm-12 col-md-6">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.measurementMethod")}
                </Typography>
                <Typography className={classes.fieldValue}>
                  {getMeasurementLabel(goal.measurementMethod)}
                </Typography>
              </Box>
            </div>

            <div className="col-sm-12 col-md-6">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.linkedQuestionPrompt")}
                </Typography>
                <Typography className={classes.fieldValue}>
                  {getLinkedQuestionLabel(goal.questionId)}
                </Typography>
              </Box>
            </div>

            <div className="col-sm-12 col-md-6">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.targetDate")}
                </Typography>
                <Typography className={classes.fieldValue}>
                  {goal.targetDate ? moment(goal.targetDate).format("DD MMM YYYY") : "-"}
                </Typography>
              </Box>
            </div>

            <div className="col-sm-12 col-md-6">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.status")}
                </Typography>
                <Box style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 12px",
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 600,
                  backgroundColor: goal.status === GOAL_STATUS.ACTIVE ? "#DBEAFE" 
                    : goal.status === GOAL_STATUS.ACHIEVED ? "#D1FAE5" 
                    : goal.status === GOAL_STATUS.DISCONTINUED ? "#FEE2E2" 
                    : "#F3F4F6",
                  color: goal.status === GOAL_STATUS.ACTIVE ? "#1E40AF" 
                    : goal.status === GOAL_STATUS.ACHIEVED ? "#065F46" 
                    : goal.status === GOAL_STATUS.DISCONTINUED ? "#991B1B" 
                    : "#374151",
                }}>
                  {t(`goalStatus.${goal.status}`)}
                </Box>
              </Box>
            </div>

            {goal.measurementMethod === MEASUREMENT_METHOD.FREQUENCY_COUNT && (
              <div className="col-sm-12 col-md-6">
                <Box className={classes.fieldContainer}>
                  <Typography className={classes.fieldLabel}>
                    {t("goal.targetValue")}
                  </Typography>
                  <Typography className={classes.fieldValue}>
                    {goal.targetValue || "-"}
                  </Typography>
                </Box>
              </div>
            )}

            {goal.measurementMethod === MEASUREMENT_METHOD.PERCENTAGE && (
              <div className="col-sm-12 col-md-6">
                <Box className={classes.fieldContainer}>
                  <Typography className={classes.fieldLabel}>
                    {t("goal.progressPercentage")}
                  </Typography>
                  <Typography className={classes.fieldValue}>
                    {goal.progressPercentage !== undefined && goal.progressPercentage !== null 
                      ? `${Number(parseFloat(goal.progressPercentage).toFixed(2))}%` 
                      : "-"}
                  </Typography>
                </Box>
              </div>
            )}

            <div className="col-sm-12 col-md-12">
              <Box className={classes.fieldContainer}>
                <Typography className={classes.fieldLabel}>
                  {t("goal.description")}
                </Typography>
                <Typography className={classes.fieldValue}>
                  {goal.description || "-"}
                </Typography>
              </Box>
            </div>
          </div>

          {goal.measurementMethod === MEASUREMENT_METHOD.MILESTONE && goal.milestones?.length > 0 && (
            <>
              <Typography className={classes.sectionTitle}>
                {t("goal.section_milestones", "Milestones")}
              </Typography>
              <Box>
                {goal.milestones.map((milestone, index) => (
                  <Box key={index} className={classes.milestoneRow}>
                    <Box className={`${classes.milestoneIcon} ${milestone.isCompleted ? classes.milestoneCompleted : classes.milestonePending}`}>
                      {milestone.isCompleted ? <i className="fas fa-check"></i> : <i className="far fa-circle"></i>}
                    </Box>
                    <Typography style={{ fontSize: 14, color: "#111827" }}>
                      {milestone.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>

        <Box className={classes.control}>
          <AppButton
            label={t("button.close")}
            onClick={onClose}
            buttonColor="#F3F4F6"
            rounder
            style={{ color: "#111827" }}
          />
        </Box>
      </Box>
    </AppDialog>
  );
};

export default ViewGoalDetailModal;
